import { LegacyRef, SyntheticEvent, forwardRef } from 'react'
import { motion } from 'framer-motion'
import Image from 'next/image'
import { useIsClient } from '@/hooks'
import { getExtension } from '@/libs/utils'
import type { ArtworkType } from '@/@types/microcms-type'

type MediaProps = {
    media: ArtworkType['thumbnail_image'] | ArtworkType['thumbnail_video']
    altText?: string
    sizes?: string
    quality?: number
    className?: string
    priority?: boolean
    props?: any
}

const MediaMotion = forwardRef<HTMLElement, MediaProps>(
    ({ media, altText, sizes, quality, className, priority, props }, ref) => {
        const isClient = useIsClient()

        const isImage = typeof media === 'object' && 'url' in media
        const isVideo = typeof media === 'object' && 'video' in media

        const styles = {
            common: `img-skelton opacity-0 data-[loaded='done']:opacity-100`
        }

        const mediaProps = isImage
            ? {
                  src: media.url,
                  width: media.width ?? 1,
                  height: media.height ?? 1,
                  alt: altText ?? '',
                  sizes: sizes ?? '100vw',
                  quality: quality ?? 75,
                  className: `${className} ${styles.common}`,
                  priority: priority ?? false,
                  ...props
              }
            : isVideo
            ? {
                  src: media.video!.url,
                  type: `video/${getExtension(media.video!.url)}`,
                  width: media.poster?.width ?? 1,
                  height: media.poster?.height ?? 1,
                  alt: altText ?? '',
                  sizes: sizes ?? '100vw',
                  quality: quality ?? 75,
                  poster:
                      media.poster &&
                      media.poster?.url + '?w=640&q=100&fm=webp',
                  controls: false,
                  muted: true,
                  autoPlay: true,
                  playsInline: true,
                  loop: true,
                  className: `${className} ${styles.common}`,
                  ...props
              }
            : {}

        const handleLoading = (e: HTMLImageElement) => {
            e.setAttribute('data-loaded', 'done')
        }

        const handleVideoLoading = (
            e: SyntheticEvent<HTMLVideoElement, Event>
        ) => {
            const video = e.target as HTMLVideoElement
            video.setAttribute('data-loaded', 'done')
        }

        return (
            <div ref={ref as LegacyRef<HTMLDivElement>}>
                {isImage ? (
                    // @ts-ignore
                    //eslint-disable-next-line jsx-a11y/alt-text
                    <Image
                        {...mediaProps}
                        onLoadingComplete={(e) => handleLoading(e)}
                    />
                ) : isVideo && isClient ? (
                    <video
                        {...mediaProps}
                        onCanPlay={(e) => handleVideoLoading(e)}
                    />
                ) : null}
            </div>
        )
    }
)

export const Media = motion(MediaMotion)
